


























































































import { Component, Prop, Mixins, Watch } from "vue-property-decorator";
import PaymentProvider from "@/components/app/global/payments/paymentProvider.vue";
import HasValidation from "@/mixins/HasValidation";
import { IClient } from "@/models/clients";
import { DataModules } from "@/store/modules/data";
import { ICurrency } from "@/models/constants";
import { mapState } from "vuex";
import { IState } from "@/store";
import { IWalletBalance } from "@/models/wallets";
import CurrenciesStore from "@/mixins/constants/currenciesStore";
import { IAccount } from "@/models/accounts";
import { QUERY_PARAMS } from "@/data/constants";
import { Location as VueLocation } from "vue-router";
import { IPaymentDetail } from "@/models/paymentDetails";
import { GatewayContext } from "@/data/enums/gateway";
import { ClientRoutes } from "@/data/enums/router";

@Component({
  components: {
    "select-payment-method": () =>
      import("@/components/app/global/payments/selectPaymentMethodComp.vue")
  },
  computed: {
    ...mapState({
      dataScope(state: IState, getters) {
        return getters[`data/${this.dataModule}/balanceScope`](this.accountId);
      },
      wallet(state, getters) {
        return getters[`data/single`]({
          storeModule: this.dataModule,
          scope: this.dataScope
        });
      }
    })
  }
})
export default class TopUpWalletModal extends Mixins(
  PaymentProvider,
  CurrenciesStore,
  HasValidation
) {
  @Prop({ required: true })
  public client!: IClient;

  @Prop({ default: "" })
  public preselectedStoredPaymentMethodId!: IPaymentDetail["id"];

  public currency: ICurrency = {} as ICurrency;
  public wallet!: IWalletBalance;
  public dataScope!: string;

  // Data
  public paymentProcessing = false;
  public amount = null;
  public minValue = 0.01;
  public dataModule = DataModules.WALLETS;
  public isLoading = false;
  public isReloading = false;
  public requestError = false;
  public canSeeWallet = this.$userCan("wallet_admin_get_balance");

  @Watch("currenciesLoaded")
  public currenciesLoadedWatcher() {
    if (this.currencyCodes.length === 1) {
      this.currency = this.$_.find(this.currencies, [
        "code",
        this.currencyCodes[0]
      ]) as ICurrency;
    }
    this.currency = this.$_.find(this.currencies, [
      "id",
      this.client.accounts[0].currency_id
    ]) as ICurrency;
  }

  public async created() {
    if (!this.canSeeWallet) {
      return;
    }
    this.isLoading = true;
    this.getBalance();
  }

  public reloadData() {
    this.isReloading = true;
    this.getBalance();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: this.dataModule,
      scope: this.dataScope,
      vm: this
    });
  }

  // Getters
  public get accountId(): IAccount["id"] {
    return this.client.accounts[0].id;
  }

  public get selectPaymentMethodBindings() {
    return {
      autoSelectFirstStoredPaymentMethod: !this
        .preselectedStoredPaymentMethodId,
      brandId: this.client.brand_id,
      canChangeManualPaymentAmount: false,
      cardHolderName: this.client.fullname,
      clientId: this.client.id,
      currencyId: this.currency.id,
      currencyCode: this.currency?.code,
      externalAuthReturnLocation: this.externalAuthReturnLocation,
      fullAmountFormatted: this.$t("_.now"),
      gatewayContext: GatewayContext.PAY,
      hasManualPayment: this.isAdmin,
      hasPayLater: false,
      isDisabled: this.paymentProcessing,
      onlineGatewaysOnly: true,
      preselectedStoredPaymentMethodId: this.preselectedStoredPaymentMethodId,
      showManualAmountInput: false,
      totalAmount: this.totalAmountToPay
    };
  }

  // Override
  public get externalAuthReturnLocation(): VueLocation {
    return this.buildResolver({
      returnLocation: {
        name: this.isAdmin
          ? "adminClientAccountCredit"
          : ClientRoutes.ACCOUNT_CREDIT,
        params: {
          clientId: this.client.id
        }
      },
      paymentMethodType: this.paymentMethodType,
      initPay: this.isExternalStore ? { clientId: this.client.id } : undefined
    }).location;
  }

  // Methods
  public async getBalance() {
    this.isLoading = true;
    this.requestError = false;
    try {
      await this.$store.dispatch(`data/${this.dataModule}/balance`, {
        scope: this.dataScope,
        accountId: this.accountId,
        ignoreStored: true,
        vm: this
      });
    } catch (error) {
      this.requestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public get additionalManualPaymentData() {
    return {
      client_id: this.client.id,
      account_id: this.accountId,
      currency_id: this.currency.id
    };
  }

  public get additionalPaymentData() {
    const returnUrl = window.location.href.split("?")[0];
    const returnUrlEncodedSuccess = encodeURIComponent(
      `${returnUrl}?${QUERY_PARAMS.PAYMENT_SUCCESS}=true`
    );
    const returnUrlEncodedFail = encodeURIComponent(
      `${returnUrl}?${QUERY_PARAMS.PAYMENT_SUCCESS}=false`
    );

    return {
      client_id: this.client.id,
      account_id: this.accountId,
      currency_id: this.currency.id,
      return_url: `?${QUERY_PARAMS.SUCCESS}=${returnUrlEncodedSuccess}&${QUERY_PARAMS.FAILED}=${returnUrlEncodedFail}`,
      cancel_url: returnUrl
    };
  }

  public async topUp() {
    this.paymentProcessing = true;
    try {
      if (!(await this.validate())) throw this.$t("_.form_invalid");
      await this.preparePaymentData(this.client.id);
      if (this.externalAuth)
        return await this.redirectToExternalAuth(this.externalAuth);
      await this.executePayment();
      this.paymentProcessing = false;
      this.$emit("success");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.paymentProcessing = false;
    }
  }

  public get currencyCodes() {
    return this.$_.isEmpty(this.wallet) ? [] : Object.keys(this.wallet.total);
  }

  public get totalAmountToPay() {
    return this.$_.toNumber(this.amount);
  }
}
